import React from 'react';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Slider } from '@/components/ui/slider';
import { 
  Sun, Moon, Monitor, Eye, Type, Zap, Palette, 
  Volume2, MousePointer, Keyboard, ChevronRight 
} from 'lucide-react';

export function AccessibilitySettings() {
  const { 
    theme, 
    setTheme, 
    toggleTheme, 
    useSystemTheme, 
    setUseSystemTheme,
    highContrast,
    setHighContrast,
    accessibility, 
    updateAccessibility,
    isDark 
  } = useTheme();

  const fontSizeLabels = {
    small: 'Small (14px)',
    medium: 'Medium (16px)',
    large: 'Large (18px)',
    xlarge: 'Extra Large (20px)'
  };

  const colorBlindModes = [
    { value: 'none', label: 'None' },
    { value: 'deuteranopia', label: 'Deuteranopia (Red-Green)' },
    { value: 'protanopia', label: 'Protanopia (Red-Green)' },
    { value: 'tritanopia', label: 'Tritanopia (Blue-Yellow)' }
  ];

  return (
    <div className="space-y-6" data-testid="accessibility-settings">
      {/* Theme Selection */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Palette className="w-5 h-5" />
            Theme & Appearance
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Theme Buttons */}
          <div className="space-y-3">
            <Label>Color Theme</Label>
            <div className="flex gap-2">
              <Button
                variant={!useSystemTheme && theme === 'light' ? 'default' : 'outline'}
                onClick={() => { setTheme('light'); setUseSystemTheme(false); }}
                className="flex-1"
                data-testid="theme-light"
              >
                <Sun className="w-4 h-4 mr-2" />
                Light
              </Button>
              <Button
                variant={!useSystemTheme && theme === 'dark' ? 'default' : 'outline'}
                onClick={() => { setTheme('dark'); setUseSystemTheme(false); }}
                className="flex-1"
                data-testid="theme-dark"
              >
                <Moon className="w-4 h-4 mr-2" />
                Dark
              </Button>
              <Button
                variant={useSystemTheme ? 'default' : 'outline'}
                onClick={() => setUseSystemTheme(true)}
                className="flex-1"
                data-testid="theme-system"
              >
                <Monitor className="w-4 h-4 mr-2" />
                System
              </Button>
            </div>
          </div>

          {/* High Contrast */}
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="high-contrast">High Contrast Mode</Label>
              <p className="text-sm text-muted-foreground">
                Increase contrast for better visibility
              </p>
            </div>
            <Switch
              id="high-contrast"
              checked={theme === 'highContrast'}
              onCheckedChange={(checked) => {
                setTheme(checked ? 'highContrast' : 'light');
                setUseSystemTheme(false);
              }}
              data-testid="high-contrast-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Text & Display */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Type className="w-5 h-5" />
            Text & Display
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Font Size */}
          <div className="space-y-3">
            <Label>Font Size</Label>
            <Select
              value={accessibility.fontSize}
              onValueChange={(value) => updateAccessibility({ fontSize: value })}
            >
              <SelectTrigger data-testid="font-size-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(fontSizeLabels).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-sm" style={{ fontSize: 'var(--base-font-size)' }}>
              Preview: The quick brown fox jumps over the lazy dog.
            </p>
          </div>

          {/* Color Blind Mode */}
          <div className="space-y-3">
            <Label>Color Blind Mode</Label>
            <Select
              value={accessibility.colorBlindMode}
              onValueChange={(value) => updateAccessibility({ colorBlindMode: value })}
            >
              <SelectTrigger data-testid="color-blind-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {colorBlindModes.map(({ value, label }) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Motion & Animation */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Zap className="w-5 h-5" />
            Motion & Animation
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="reduce-motion">Reduce Motion</Label>
              <p className="text-sm text-muted-foreground">
                Minimize animations and transitions
              </p>
            </div>
            <Switch
              id="reduce-motion"
              checked={accessibility.reduceMotion}
              onCheckedChange={(checked) => updateAccessibility({ reduceMotion: checked })}
              data-testid="reduce-motion-toggle"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="reduce-transparency">Reduce Transparency</Label>
              <p className="text-sm text-muted-foreground">
                Reduce blur and transparency effects
              </p>
            </div>
            <Switch
              id="reduce-transparency"
              checked={accessibility.reduceTransparency}
              onCheckedChange={(checked) => updateAccessibility({ reduceTransparency: checked })}
              data-testid="reduce-transparency-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Interaction */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MousePointer className="w-5 h-5" />
            Interaction
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="keyboard-nav">Enhanced Keyboard Navigation</Label>
              <p className="text-sm text-muted-foreground">
                Improved focus indicators and shortcuts
              </p>
            </div>
            <Switch
              id="keyboard-nav"
              checked={accessibility.keyboardNavigation}
              onCheckedChange={(checked) => updateAccessibility({ keyboardNavigation: checked })}
              data-testid="keyboard-nav-toggle"
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="screen-reader">Screen Reader Mode</Label>
              <p className="text-sm text-muted-foreground">
                Optimize for screen reader software
              </p>
            </div>
            <Switch
              id="screen-reader"
              checked={accessibility.screenReaderMode}
              onCheckedChange={(checked) => updateAccessibility({ screenReaderMode: checked })}
              data-testid="screen-reader-toggle"
            />
          </div>
        </CardContent>
      </Card>

      {/* Current Settings Summary */}
      <Card className="bg-muted/50">
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">Current Settings</p>
              <p className="text-sm text-muted-foreground">
                Theme: {isDark ? 'Dark' : 'Light'} • 
                Font: {accessibility.fontSize} • 
                {accessibility.reduceMotion ? 'Reduced Motion' : 'Standard Motion'}
              </p>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                updateAccessibility({
                  fontSize: 'medium',
                  reduceMotion: false,
                  reduceTransparency: false,
                  screenReaderMode: false,
                  keyboardNavigation: true,
                  colorBlindMode: 'none'
                });
                setUseSystemTheme(true);
                if (typeof setHighContrast === 'function') {
                  setHighContrast(false);
                }
              }}
              data-testid="reset-accessibility"
            >
              Reset to Defaults
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default AccessibilitySettings;
